import { useDebouncedValue } from '@community_dev/hooks'
import { Button, Checkbox, Divider, Input, Radio, Skeleton, Space, Typography } from 'antd'
import debounce from 'lodash/debounce'
import React, { useEffect, useMemo, useState, type JSX } from 'react'

import { useActorsCatalog, useCategories, useClientsCatalog } from './queries'
import { FilterAction, FilterState } from './state'
import { colorFor, humanize, TIME_PRESETS } from './utils'

const { Text } = Typography

const SEARCH_DEBOUNCE_MS = 300

type Props = {
  state: FilterState
  dispatch: React.Dispatch<FilterAction>
}

export function FilterSidebar({ state, dispatch }: Props): JSX.Element {
  const [qInput, setQInput] = useState(state.q)
  const [clientQ, setClientQ] = useState('')
  const [actorQ, setActorQ] = useState('')
  const debouncedClientQ = useDebouncedValue(clientQ, SEARCH_DEBOUNCE_MS)
  const debouncedActorQ = useDebouncedValue(actorQ, SEARCH_DEBOUNCE_MS)

  const debouncedSetQ = useMemo(
    () => debounce((q: string) => dispatch({ type: 'set_q', q }), SEARCH_DEBOUNCE_MS),
    [dispatch],
  )
  useEffect(() => () => debouncedSetQ.cancel(), [debouncedSetQ])

  const { data: categories, isLoading: catLoading } = useCategories()
  const { data: clientPage } = useClientsCatalog({ q: debouncedClientQ, limit: 50 })
  const { data: actorPage } = useActorsCatalog({ q: debouncedActorQ, limit: 50 })

  return (
    <div style={{ padding: 16 }}>
      <Input.Search
        allowClear
        onChange={(e) => {
          setQInput(e.target.value)
          debouncedSetQ(e.target.value)
        }}
        placeholder="Search events…"
        value={qInput}
      />

      <Section
        onReset={() => dispatch({ type: 'set_category', key: null })}
        showReset={!!state.category}
        title="Category"
      >
        {catLoading ? (
          <Skeleton active paragraph={{ rows: 3 }} title={false} />
        ) : (
          <Radio.Group
            onChange={(e) => dispatch({ type: 'set_category', key: e.target.value || null })}
            style={{ display: 'flex', flexDirection: 'column', gap: 4 }}
            value={state.category || ''}
          >
            <Radio value="">All categories</Radio>
            {(categories || []).map((c) => {
              const color = colorFor(c.key)
              return (
                <Radio key={c.key} value={c.key}>
                  <Space size={6}>
                    <span
                      style={{
                        background: color.fg,
                        borderRadius: 2,
                        display: 'inline-block',
                        height: 8,
                        width: 8,
                      }}
                    />
                    <span>{c.label || humanize(c.key)}</span>
                    <Text style={{ fontSize: 11 }} type="secondary">
                      {Array.isArray(c.activityTypes) ? c.activityTypes.length : 0}
                    </Text>
                  </Space>
                </Radio>
              )
            })}
          </Radio.Group>
        )}
      </Section>

      <Section title="Time period">
        <Radio.Group
          onChange={(e) => dispatch({ type: 'set_time', key: e.target.value || null })}
          style={{ display: 'flex', flexDirection: 'column', gap: 4 }}
          value={state.timeKey || ''}
        >
          <Radio value="">Any time</Radio>
          {TIME_PRESETS.map((p) => (
            <Radio key={p.key} value={p.key}>
              {p.label}
            </Radio>
          ))}
        </Radio.Group>
      </Section>

      <Section
        onReset={() => dispatch({ type: 'reset_clients' })}
        showReset={state.clientIds.length > 0}
        title="Client / account"
      >
        <Input.Search
          allowClear
          onChange={(e) => setClientQ(e.target.value)}
          placeholder="Search clients…"
          style={{ marginBottom: 8 }}
          value={clientQ}
        />
        <div style={{ maxHeight: 280, overflowY: 'auto' }}>
          <Checkbox.Group
            onChange={(ids) => dispatch({ type: 'set_clients', ids: (ids as string[]) || [] })}
            style={{ display: 'flex', flexDirection: 'column', gap: 4 }}
            value={state.clientIds}
          >
            {(clientPage?.data || []).map((c) => (
              <Checkbox key={c.id} value={c.id}>
                {c.name || c.id}
              </Checkbox>
            ))}
          </Checkbox.Group>
          {(clientPage?.data || []).length === 0 && <Text type="secondary">No clients</Text>}
        </div>
      </Section>

      <Section
        onReset={() => dispatch({ type: 'reset_actors' })}
        showReset={state.actorIds.length > 0 || state.excludeSystemActors}
        title="Actor"
      >
        <Checkbox
          checked={state.excludeSystemActors}
          onChange={(e) => dispatch({ type: 'set_exclude_system', value: e.target.checked })}
          style={{ marginBottom: 8 }}
        >
          <Text italic>Hide system</Text>
        </Checkbox>
        <Input.Search
          allowClear
          onChange={(e) => setActorQ(e.target.value)}
          placeholder="Search actors…"
          style={{ marginBottom: 8 }}
          value={actorQ}
        />
        <div style={{ maxHeight: 280, overflowY: 'auto' }}>
          <Checkbox.Group
            onChange={(ids) => dispatch({ type: 'set_actors', ids: (ids as string[]) || [] })}
            style={{ display: 'flex', flexDirection: 'column', gap: 4 }}
            value={state.actorIds}
          >
            {(actorPage?.data || []).map((a) => {
              const id = a.seatId || a.id
              const label = a.name || a.email || id || '(unnamed)'
              return (
                <Checkbox key={id} value={id}>
                  {label}
                </Checkbox>
              )
            })}
          </Checkbox.Group>
          {(actorPage?.data || []).length === 0 && <Text type="secondary">No actors</Text>}
        </div>
      </Section>
    </div>
  )
}

function Section({
  title,
  showReset,
  onReset,
  children,
}: {
  title: string
  showReset?: boolean
  onReset?: () => void
  children: React.ReactNode
}): JSX.Element {
  return (
    <>
      <Divider style={{ margin: '12px 0' }} />
      <div
        style={{
          alignItems: 'center',
          display: 'flex',
          justifyContent: 'space-between',
          marginBottom: 8,
        }}
      >
        <Text strong style={{ fontSize: 11, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
          {title}
        </Text>
        {showReset ? (
          <Button onClick={onReset} size="small" type="link">
            Reset
          </Button>
        ) : null}
      </div>
      {children}
    </>
  )
}
